import type { InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';
import { cn } from '@/lib/cn';

/**
 * Input.
 *
 * §11: errors "say what happened and never apologise vaguely". The error slot
 * is rendered by the field itself and wired to aria-describedby, so a screen
 * reader announces the problem with the input rather than leaving it stranded
 * as red text somewhere below.
 */

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  /**
   * React 19 passes `ref` as an ordinary prop, so no forwardRef wrapper is
   * needed — it just has to be declared. The OTP screen focuses the code field
   * the moment the code is sent, which is the difference between one tap and
   * three.
   */
  ref?: React.Ref<HTMLInputElement>;
  label?: string;
  hint?: ReactNode;
  error?: string | null;
  /** Monospace, for codes, batch numbers and phone numbers. */
  mono?: boolean;
  /** Rendered inside the field, before the value. Named to avoid the
   * deprecated RDFa `prefix` HTML attribute, which is typed as a string. */
  adornment?: ReactNode;
}

export function Input({
  ref,
  label,
  hint,
  error,
  mono = false,
  adornment,
  className,
  id,
  ...props
}: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="w-full">
      {label ? (
        <label
          htmlFor={inputId}
          className="mb-[5px] block text-[11px] font-bold tracking-[0.01em] text-muted"
        >
          {label}
        </label>
      ) : null}

      <div
        className={cn(
          'flex min-h-[42px] items-center gap-2 rounded-inp border bg-white px-3 py-[11px]',
          'focus-within:border-blue focus-within:ring-2 focus-within:ring-blue/15',
          error ? 'border-danger' : 'border-line',
          className,
        )}
      >
        {adornment ? <span className="shrink-0 text-[13px] text-muted">{adornment}</span> : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'w-full border-0 bg-transparent p-0 text-[13px] text-ink outline-none',
            'placeholder:text-faint',
            mono && 'font-mono tracking-[0.03em] tabular',
          )}
          {...props}
        />
      </div>

      {error ? (
        <p id={`${inputId}-error`} className="mt-1.5 text-[11px] font-medium text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="mt-1.5 text-[11px] text-faint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
